import type { Grid } from "../core/Grid.ts";
import { CellType } from "../core/types.ts";
import type { EventBus } from "../core/EventBus.ts";
import type { PathRegistry } from "../core/PathRegistry.ts";
import type { RuntimeState } from "./types.ts";
import type { Tower } from "../entities/Tower.ts";
import {
  SELL_MULTIPLIER,
  type EconomySystem,
} from "../systems/EconomySystem.ts";
import type { SynergySystem } from "../systems/SynergySystem.ts";
import type { WaveManager } from "../systems/WaveManager.ts";
import type { SellConfirmDialog } from "../ui/SellConfirmDialog.ts";
import { setSelectedTower } from "./stateIntents.ts";
import { rerouteAllPaths } from "../systems/PathRerouter.ts";

export interface TowerActionDeps {
  gs: Pick<RuntimeState, "towers" | "selectedTower" | "pathPixels">;
  economy: EconomySystem;
  grid: Grid;
  synergySystem: SynergySystem;
  waveManager: WaveManager;
  sellDialog: SellConfirmDialog;
  eventBus: EventBus;
  recalculatePath: () => void;
  // GA-15 fix: needed so sell() can reroute already-spawned enemies off a
  // now-unnecessary detour — mirrors tower/firewall PLACEMENT (placementPorts.ts).
  pathRegistry: PathRegistry;
}

/** Centralizes tower upgrade/sell transactions outside of render(). */
export class TowerActionController {
  constructor(private readonly deps: TowerActionDeps) {}

  upgrade(tower: Tower): void {
    if (tower.level >= tower.maxLevel) return;
    const cost = tower.getUpgradeCost();
    if (!this.deps.economy.canAfford(cost)) return;
    this.deps.economy.spend(cost);
    tower.upgrade();
    this.deps.eventBus.emit("tower.upgraded", { tower });
    // Force panel refresh by clearing cached id
    setSelectedTower(this.deps.gs, null);
    setSelectedTower(this.deps.gs, tower);
  }

  /** CR-4: perform the sell transaction directly (no dialog). The inline
   *  confirm in the tower panel calls this; requestSell keeps the legacy modal. */
  sell(tower: Tower): void {
    const sellPrice = this.deps.economy.getSellPrice(tower.totalInvested);
    const idx = this.deps.gs.towers.findIndex((t) => t === tower);
    if (idx === -1) return;
    this.deps.gs.towers.splice(idx, 1);
    this.deps.grid.setCell(tower.col, tower.row, CellType.EMPTY);
    this.deps.economy.addGold(sellPrice);
    this.deps.recalculatePath();
    this.deps.waveManager.pathPixels = this.deps.gs.pathPixels;
    // GA-15 fix: reroute already-spawned enemies onto the freshly-reopened
    // route — mirrors commitTower/commitFirewall (placementPorts.ts), which
    // already do this on the ADDITION side of a grid mutation.
    rerouteAllPaths(this.deps.waveManager, this.deps.pathRegistry);
    this.deps.synergySystem.setTowers(this.deps.gs.towers);
    this.deps.synergySystem.recalculate();
    this.deps.eventBus.emit("tower.sold", { tower, sellPrice });
    setSelectedTower(this.deps.gs, null);
  }

  requestSell(tower: Tower): void {
    const sellPrice = this.deps.economy.getSellPrice(tower.totalInvested);
    this.deps.sellDialog.show({
      title: "DECOMMISSION TOWER",
      itemName: `${tower.element.toUpperCase()} TOWER (L${tower.level})`,
      invested: tower.totalInvested,
      refund: sellPrice,
      // BL-254: derive from the LIVE refund (FULL_REFUND changes the economy's
      // sell multiplier at runtime); the constant is only the 0-invested fallback.
      refundPercent:
        tower.totalInvested > 0
          ? Math.round((sellPrice / tower.totalInvested) * 100)
          : Math.round(SELL_MULTIPLIER * 100),
      onConfirm: () => this.sell(tower),
      onCancel: () => {},
    });
  }
}
